import AddonsSelector from './AddonsSelector';
import { ADDON_LIST, getAddonPrices } from './addons';
import { usePricing } from './usePricing';
import React, { useState, useEffect, useCallback } from 'react';

const API = 'https://api.botanix.in/api';

const C = {
 bg:'#0A1628', surface:'#0F2040', surface2:'#162A40',
 border:'rgba(30,58,95,.8)', border2:'rgba(30,58,95,1)',
 text:'#F0F2FF', text2:'#A0A8C0', text3:'#606880',
 accent:'#22C55E', red:'#EF4444', redBg:'rgba(239,68,68,.1)', redBorder:'rgba(239,68,68,.25)',
 amber:'#F59E0B', amberBg:'rgba(245,158,11,.1)', amberBorder:'rgba(245,158,11,.25)',
 blue:'#3B82F6', purple:'#A78BFA',
};

const inr = n => '₹' + Number(n||0).toLocaleString('en-IN',{maximumFractionDigits:0});

const FOOD_MENU = {
 'Breakfast': ['Poha','Upma','Idli Sambhar','Bread Butter','Boiled Eggs','Paratha','Aloo Puri','Fruit Plate','Tea','Coffee','Juice'],
 'Lunch': ['Dal Makhani','Dal Tadka','Paneer Butter Masala','Shahi Paneer','Butter Chicken','Chicken Curry','Mutton Curry','Steamed Rice','Jeera Rice','Biryani','Roti','Naan','Tandoori Roti','Raita','Salad','Papad'],
 'Hi-Tea': ['Samosa','Pakora','Bread Pakora','Sandwich','Pav Bhaji','Tea','Coffee','Juice','Cookies'],
 'Dinner': ['Dal Makhani','Paneer Butter Masala','Shahi Paneer','Butter Chicken','Mutton Rogan Josh','Chicken Biryani','Veg Biryani','Naan','Roti','Steamed Rice','Raita','Salad','Gulab Jamun','Rasgulla','Ice Cream'],
 'Snacks': ['Samosa','Pakora','Bhel Puri','Pav Bhaji','Maggi','Corn','Roasted Peanuts'],
};

const DAY_PACKAGES = [
 { id:'grp_std', name:'Group Standard', ratePerPax:1200, includes:'Welcome Drink, Lunch, Hi-Tea, Activities' },
 { id:'grp_prem', name:'Group Premium', ratePerPax:1800, includes:'All Standard + Breakfast, Dinner, Bonfire' },
 { id:'grp_full', name:'Group Full Board', ratePerPax:2500, includes:'All Meals + Activities + Evening Program' },
 { id:'custom', name:'Custom Package', ratePerPax:0, includes:'Custom as per requirement' },
];

const ROOM_TYPES_DEFAULT = [
 { id:1, name:'Forest View Room', rack_rate:4500 },
 { id:2, name:'Lakeside Suite', rack_rate:8500 },
 { id:3, name:'Canopy Cottage', rack_rate:12000 },
 { id:4, name:'Jungle Tent', rack_rate:3000 },
 { id:5, name:'Driver Room', rack_rate:800 },
];

const MEAL_PLANS = ['EP (No Meals)','CP (Breakfast)','MAP (B+D)','AP (All Meals)'];
const ACTIVITIES = ['Nature Walk','Paintball','Camel Ride','Zipline','Archery','Bullock Cart','Cycling','Bonfire','Bird Watching','Star Gazing'];
const SOURCES = ['Direct','Phone','Email','WhatsApp','Referral','OTA','Website','Corporate'];

const inp = { width:'100%', padding:'10px 12px', background:'rgba(255,255,255,.05)', border:`1px solid rgba(30,58,95,1)`, borderRadius:8, color:'#F0F2FF', fontSize:13, boxSizing:'border-box', outline:'none', fontFamily:'Inter,sans-serif' };
const sel = { ...inp, cursor:'pointer' };
const Label = ({children}) => <label style={{ display:'block', fontSize:11, color:'#A0A8C0', marginBottom:4, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>{children}</label>;

export default function GroupBooking({ onClose, onSaved, bookingToEdit }) {
 const currentUser = (() => { try { return JSON.parse(localStorage.getItem('botanix_user')); } catch { return null; } })();
 const isSales = currentUser?.roleKey === 'SALES';
 const { getRoomRate, loading: pricingLoading } = usePricing();

 const [step, setStep] = useState(1);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState('');
 const [roomTypes, setRoomTypes] = useState(ROOM_TYPES_DEFAULT);

 // Step 1 — Group Details
 const [groupName, setGroupName] = useState('');
 const [contact, setContact] = useState('');
 const [email, setEmail] = useState('');
 const [pax, setPax] = useState(20);
 const [startDate, setStartDate] = useState('');
 const [endDate, setEndDate] = useState('');
 const [source, setSource] = useState('Direct');
 const [salesperson, setSalesperson] = useState(currentUser?.name||'');
 const [status, setStatus] = useState('Tentative');
 const [groupType, setGroupType] = useState('School Group');
 const [bookingMode, setBookingMode] = useState('day'); // day | room | both

 // Step 2 — Package & Rooms
 const [selectedPkg, setSelectedPkg] = useState(DAY_PACKAGES[0]);
 const [customRate, setCustomRate] = useState('');
 const [discountPct, setDiscountPct] = useState(0);
 // Room booking
 const [roomAllocations, setRoomAllocations] = useState([
 { room_type_id:1, rooms:1, rate:4500, meal_plan:'CP (Breakfast)' }
 ]);
 const [addons, setAddons] = useState({});
 const [selectedActivities, setSelectedActivities] = useState([]);

 // Step 3 — Food Menu
 const [meals, setMeals] = useState({
 Breakfast: { enabled:false, items:[], pax:0, rate:150 },
 Lunch: { enabled:true, items:[], pax:0, rate:350 },
 HiTea: { enabled:false, items:[], pax:0, rate:120 },
 Dinner: { enabled:false, items:[], pax:0, rate:400 },
 Snacks: { enabled:false, items:[], pax:0, rate:100 },
 });

 useEffect(() => {
 fetch(`${API}/rooms/types`).then(r=>r.json()).then(d=>{if(Array.isArray(d)&&d.length) setRoomTypes(d);}).catch(()=>{});
 }, []);

 const nights = startDate && endDate ? Math.max(1, Math.ceil((new Date(endDate)-new Date(startDate))/86400000)) : 1;

 // Calculations
 const dayAmount = (bookingMode==='day'||bookingMode==='both')
 ? ((selectedPkg.id==='custom'?parseFloat(customRate||0):selectedPkg.ratePerPax) * pax) : 0;
 const roomAmount = (bookingMode==='room'||bookingMode==='both')
 ? roomAllocations.reduce((s,r)=>s+(r.rate||0)*(r.rooms||0)*nights, 0) : 0;
 const mealExtra = Object.values(meals).filter(m=>m.enabled).reduce((s,m)=>s+((m.rate||0)*(m.pax||pax)),0);
 const actAmount = selectedActivities.length * 500 * pax;
 const subtotal = dayAmount + roomAmount + mealExtra + actAmount;
 const discount = subtotal * (discountPct/100);
 const taxable = subtotal - discount;
 const gst = taxable * 0.18;
 const total = taxable + gst;

 const toggleMealItem = (meal, item) => {
 setMeals(p=>({ ...p, [meal]:{ ...p[meal], items: p[meal].items.includes(item)?p[meal].items.filter(i=>i!==item):[...p[meal].items,item] } }));
 };

 const addRoomRow = () => setRoomAllocations(p=>[...p,{room_type_id:1,rooms:1,rate:4500,meal_plan:'CP (Breakfast)'}]);
 const removeRoomRow = (i) => setRoomAllocations(p=>p.filter((_,idx)=>idx!==i));
 const updateRoom = (i,k,v) => setRoomAllocations(p=>p.map((r,idx)=>idx===i?{...r,[k]:v}:r));

 const handleSave = async () => {
 if (!groupName) { setError('Enter group name'); return; }
 if (!startDate) { setError('Select date'); return; }
 setSaving(true); setError('');
 try {
 const foodData = {
 type:'group', groupType, bookingMode,
 package: bookingMode!=='room'?selectedPkg:null,
 rooms: bookingMode!=='day'?roomAllocations:null,
 meals, activities:selectedActivities,
 ratePerPax: selectedPkg.id==='custom'?parseFloat(customRate||0):selectedPkg.ratePerPax,
 discountPct, nights,
 };
 const payload = {
 booking_type: ' Group Booking',
 guest_name: groupName,
 guest_phone: contact,
 guest_email: email,
 pax_count: parseInt(pax),
 start_date: startDate,
 end_date: endDate||startDate,
 source, salesperson,
 booking_status: status,
 total_amount: Math.round(total),
 advance_paid: 0,
 balance_amount: Math.round(total),
 notes: `<<<GROUP_DATA:${JSON.stringify(foodData)}>>>`,
 };
 const url = bookingToEdit?`${API}/bookings/${bookingToEdit.id}`:`${API}/bookings`;
 const method = bookingToEdit?'PUT':'POST';
 const r = await fetch(url,{method,headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
 if (!r.ok) throw new Error('Save failed');
 onSaved?.(); onClose?.();
 } catch(e) { setError(e.message); }
 finally { setSaving(false); }
 };

 const STEPS = ['Group Details','Packages & Rooms','Food Menu & Add-ons','Review & Save'];

 return (
 <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.85)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
 <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:16, width:'100%', maxWidth:720, maxHeight:'92vh', display:'flex', flexDirection:'column', overflow:'hidden' }}>

 {/* Header */}
 <div style={{ padding:'16px 20px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0, background:C.surface }}>
 <div>
 <div style={{ fontWeight:800, fontSize:16, color:C.text }}> {bookingToEdit?'Edit':'New'} Group Booking</div>
 <div style={{ fontSize:12, color:C.text2, marginTop:2 }}>Step {step} of 4 — {STEPS[step-1]}</div>
 </div>
 <button onClick={onClose} style={{ background:'none', border:'none', color:C.text2, fontSize:20, cursor:'pointer' }}></button>
 </div>

 {/* Step indicators */}
 <div style={{ display:'flex', padding:'12px 20px', gap:6, borderBottom:`1px solid ${C.border}`, flexShrink:0, background:C.surface }}>
 {STEPS.map((s,i)=>(
 <div key={i} style={{ flex:1, textAlign:'center', cursor:'pointer' }} onClick={()=>i<step-1&&setStep(i+1)}>
 <div style={{ width:26, height:26, borderRadius:'50%', background:step>i+1?C.accent:step===i+1?C.accent:'rgba(255,255,255,.1)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, margin:'0 auto 3px' }}>{step>i+1?'':i+1}</div>
 <div style={{ fontSize:10, color:step===i+1?C.accent:C.text3, lineHeight:1.2 }}>{s}</div>
 </div>
 ))}
 </div>

 {/* Content */}
 <div style={{ flex:1, overflowY:'auto', padding:20 }}>
 {error && <div style={{ padding:'10px 14px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:8, color:C.red, fontSize:13, marginBottom:16 }}>{error}</div>}

 {/* ── STEP 1 ── */}
 {step===1 && (
 <div style={{ display:'grid', gap:14 }}>
 {/* Booking Mode */}
 <div>
 <Label>Booking Type *</Label>
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8 }}>
 {[
 {id:'day', label:' Day Visit', desc:'Picnic, outing, event'},
 {id:'room', label:' Room Stay', desc:'Overnight group stay'},
 {id:'both', label:' Day + Rooms', desc:'Visit with accommodation'},
 ].map(m=>(
 <div key={m.id} onClick={()=>setBookingMode(m.id)}
 style={{ background:bookingMode===m.id?`${C.accent}15`:C.surface, border:`2px solid ${bookingMode===m.id?C.accent:C.border}`, borderRadius:10, padding:12, cursor:'pointer', textAlign:'center' }}>
 <div style={{ fontSize:16, marginBottom:4 }}>{m.label.split(' ')[0]}</div>
 <div style={{ fontSize:12, fontWeight:700, color:bookingMode===m.id?C.accent:C.text }}>{m.label.slice(2)}</div>
 <div style={{ fontSize:10, color:C.text3, marginTop:2 }}>{m.desc}</div>
 </div>
 ))}
 </div>
 </div>

 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
 <div>
 <Label>Group / Organisation Name *</Label>
 <input style={inp} value={groupName} onChange={e=>setGroupName(e.target.value)} placeholder="ABC School / XYZ Group" />
 </div>
 <div>
 <Label>Group Type</Label>
 <select style={sel} value={groupType} onChange={e=>setGroupType(e.target.value)}>
 {['School Group','College Group','Corporate Group','Family Group','Tour Group','NGO','Religious Group','Sports Group','Other'].map(t=><option key={t} style={{background:'#0F2040',color:'#F0F2FF'}}>{t}</option>)}
 </select>
 </div>
 </div>

 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
 <div><Label>Contact Person / Phone</Label><input style={inp} value={contact} onChange={e=>setContact(e.target.value)} placeholder="+91 98100 00000" /></div>
 <div><Label>Email</Label><input style={inp} type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="contact@group.com" /></div>
 </div>

 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
 <div><Label>{bookingMode==='room'?'Check-in Date':'Visit Date'} *</Label><input style={inp} type="date" value={startDate} onChange={e=>setStartDate(e.target.value)} /></div>
 <div><Label>{bookingMode==='room'?'Check-out Date':'End Date'}</Label><input style={inp} type="date" value={endDate} onChange={e=>setEndDate(e.target.value)} /></div>
 <div><Label>Total Pax *</Label><input style={inp} type="number" value={pax} onChange={e=>setPax(e.target.value)} min={2} /></div>
 </div>

 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12 }}>
 <div>
 <Label>Source</Label>
 <select style={sel} value={source} onChange={e=>setSource(e.target.value)}>
 {SOURCES.map(s=><option key={s} style={{background:'#0F2040',color:'#F0F2FF'}}>{s}</option>)}
 </select>
 </div>
 <div><Label>Salesperson</Label><input style={{...inp,opacity:isSales?.7:1}} value={salesperson} onChange={e=>!isSales&&setSalesperson(e.target.value)} readOnly={isSales} /></div>
 <div>
 <Label>Status</Label>
 <select style={sel} value={status} onChange={e=>setStatus(e.target.value)}>
 {['Tentative','Confirmed','Cancelled'].map(s=><option key={s} style={{background:'#0F2040',color:'#F0F2FF'}}>{s}</option>)}
 </select>
 </div>
 </div>
 </div>
 )}

 {/* ── STEP 2: PACKAGES & ROOMS ── */}
 {step===2 && (
 <div>
 {/* Day Package — if day or both */}
 {(bookingMode==='day'||bookingMode==='both') && (
 <div style={{ marginBottom:20 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.accent, marginBottom:10 }}> Day Visit Package</div>
 <div style={{ display:'grid', gap:8, marginBottom:12 }}>
 {DAY_PACKAGES.map(pkg=>(
 <div key={pkg.id} onClick={()=>setSelectedPkg(pkg)}
 style={{ background:selectedPkg.id===pkg.id?`${C.accent}15`:C.surface, border:`2px solid ${selectedPkg.id===pkg.id?C.accent:C.border}`, borderRadius:10, padding:14, cursor:'pointer', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
 <div>
 <div style={{ fontWeight:700, fontSize:13, color:C.text }}>{pkg.name}</div>
 <div style={{ fontSize:11, color:C.text2, marginTop:2 }}>{pkg.includes}</div>
 </div>
 <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
 {pkg.id!=='custom'
 ? <div style={{ fontSize:18, fontWeight:900, color:C.accent }}>{inr(pkg.ratePerPax)}<span style={{ fontSize:10, color:C.text3 }}>/pax</span></div>
 : <div style={{ fontSize:12, color:C.amber }}>Custom rate</div>}
 </div>
 </div>
 ))}
 </div>
 {selectedPkg.id==='custom' && (
 <div style={{ marginBottom:12 }}>
 <Label>Custom Rate per Pax (₹)</Label>
 <input style={inp} type="number" value={customRate} onChange={e=>setCustomRate(e.target.value)} placeholder="Enter rate per person" />
 </div>
 )}
 </div>
 )}

 {/* Room Allocation — if room or both */}
 {(bookingMode==='room'||bookingMode==='both') && (
 <div style={{ marginBottom:20 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.blue }}> Room Allocation ({nights} night{nights>1?'s':''})</div>
 <button onClick={addRoomRow} style={{ padding:'5px 12px', background:`${C.accent}20`, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, cursor:'pointer', fontSize:12, fontWeight:700 }}>+ Add Room Type</button>
 </div>
 {roomAllocations.map((r,i)=>(
 <div key={i} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:14, marginBottom:8 }}>
 <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 2fr auto', gap:8, alignItems:'end' }}>
 <div>
 <Label>Room Type</Label>
 <select style={sel} value={r.room_type_id}
 onChange={e=>{
 const rt=roomTypes.find(t=>t.id===parseInt(e.target.value));
 updateRoom(i,'room_type_id',parseInt(e.target.value));
 if(rt) updateRoom(i,'rate',rt.getRoomRate(rt.id, startDate)||rt.rack_rate||rt.rate||0);
 }}>
 {roomTypes.map(rt=><option key={rt.id} value={rt.id} style={{background:'#0F2040',color:'#F0F2FF'}}>{rt.name}</option>)}
 </select>
 </div>
 <div>
 <Label>No. of Rooms</Label>
 <input style={inp} type="number" value={r.rooms} onChange={e=>updateRoom(i,'rooms',parseInt(e.target.value)||1)} min={1} />
 </div>
 <div>
 <Label>Rate/Room/Night</Label>
 <input style={inp} type="number" value={r.rate} onChange={e=>updateRoom(i,'rate',parseFloat(e.target.value)||0)} />
 </div>
 <div>
 <Label>Meal Plan</Label>
 <select style={sel} value={r.meal_plan} onChange={e=>updateRoom(i,'meal_plan',e.target.value)}>
 {MEAL_PLANS.map(m=><option key={m} style={{background:'#0F2040',color:'#F0F2FF'}}>{m}</option>)}
 </select>
 </div>
 <button onClick={()=>removeRoomRow(i)} style={{ padding:'8px 10px', background:C.redBg, border:`1px solid ${C.redBorder}`, borderRadius:6, color:C.red, cursor:'pointer', fontSize:12 }}></button>
 </div>
 <div style={{ fontSize:12, color:C.accent, marginTop:6, textAlign:'right' }}>
 Subtotal: {inr(r.rate*r.rooms*nights)} ({r.rooms} rooms × {nights} nights)
 </div>
 </div>
 ))}
 </div>
 )}

 {/* Activities */}
 <div style={{ marginBottom:16 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.amber, marginBottom:10 }}> Activities (₹500/pax each)</div>
 <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
 {ACTIVITIES.map(a=>(
 <span key={a} onClick={()=>setSelectedActivities(p=>p.includes(a)?p.filter(x=>x!==a):[...p,a])}
 style={{ padding:'5px 12px', borderRadius:20, fontSize:12, cursor:'pointer', fontWeight:500,
 background:selectedActivities.includes(a)?C.amber:'rgba(255,255,255,.06)',
 color:selectedActivities.includes(a)?'#fff':C.text2,
 border:`1px solid ${selectedActivities.includes(a)?C.amber:C.border}` }}>
 {a}
 </span>
 ))}
 </div>
 </div>

 {/* Discount */}
 <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
 <div>
 <Label>Discount % (max 20%)</Label>
 <input style={inp} type="number" value={discountPct} onChange={e=>setDiscountPct(Math.min(20,Math.max(0,parseFloat(e.target.value)||0)))} min={0} max={20} />
 </div>
 <div style={{ background:`${C.accent}10`, border:`1px solid ${C.accent}33`, borderRadius:8, padding:12 }}>
 <div style={{ fontSize:11, color:C.text3 }}>Estimated Total ({pax} pax{bookingMode!=='day'?`, ${nights} nights`:''})</div>
 <div style={{ fontSize:22, fontWeight:900, color:C.accent }}>{inr(total)}</div>
 <div style={{ fontSize:11, color:C.text3 }}>incl. 18% GST</div>
 </div>
 </div>
 </div>
 )}

 {/* ── STEP 3: FOOD MENU ── */}
 {step===3 && (
 <div>
 <div style={{ fontSize:13, color:C.text2, marginBottom:14 }}>Select meals and menu items for the group. Prices are per person.</div>
 {Object.entries(FOOD_MENU).map(([meal, items])=>{
 const mKey = meal.replace('-','').replace(' ','');
 const mData = meals[mKey] || { enabled:false, items:[], pax:0, rate:0 };
 return (
 <div key={meal} style={{ background:C.surface, border:`1px solid ${mData.enabled?C.accent:C.border}`, borderRadius:12, padding:16, marginBottom:10 }}>
 <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:mData.enabled?12:0 }}>
 <div style={{ display:'flex', alignItems:'center', gap:10 }}>
 <input type="checkbox" checked={!!mData.enabled}
 onChange={e=>setMeals(p=>({...p,[mKey]:{...p[mKey],enabled:e.target.checked,pax:p[mKey].pax||parseInt(pax)}}))}
 style={{ width:16, height:16, cursor:'pointer', accentColor:C.accent }} />
 <div style={{ fontWeight:700, fontSize:14, color:mData.enabled?C.accent:C.text }}>{meal}</div>
 </div>
 {mData.enabled && (
 <div style={{ display:'flex', gap:8, alignItems:'center' }}>
 <div><span style={{ fontSize:10, color:C.text3 }}>Pax: </span>
 <input type="number" value={mData.pax||pax} onChange={e=>setMeals(p=>({...p,[mKey]:{...p[mKey],pax:parseInt(e.target.value)||0}}))}
 style={{ ...inp, width:70, padding:'4px 8px', display:'inline-block' }} />
 </div>
 <div><span style={{ fontSize:10, color:C.text3 }}>₹/pax: </span>
 <input type="number" value={mData.rate} onChange={e=>setMeals(p=>({...p,[mKey]:{...p[mKey],rate:parseInt(e.target.value)||0}}))}
 style={{ ...inp, width:80, padding:'4px 8px', display:'inline-block' }} />
 </div>
 </div>
 )}
 </div>
 {mData.enabled && (
 <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
 {items.map(item=>(
 <span key={item} onClick={()=>toggleMealItem(mKey, item)}
 style={{ padding:'4px 10px', borderRadius:20, fontSize:11, cursor:'pointer', fontWeight:500,
 background:mData.items.includes(item)?C.accent:'rgba(255,255,255,.06)',
 color:mData.items.includes(item)?'#fff':C.text2,
 border:`1px solid ${mData.items.includes(item)?C.accent:C.border}` }}>
 {item}
 </span>
 ))}
 </div>
 )}
 </div>
 );
 })}
 </div>
 )}

 {/* ── STEP 4: REVIEW ── */}
 {step===4 && (
 <div>
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:18, marginBottom:14 }}>
 <div style={{ fontWeight:700, fontSize:15, color:C.text, marginBottom:12 }}> Booking Summary</div>
 {[
 ['Group Name', groupName],
 ['Group Type', groupType],
 ['Booking Mode', bookingMode==='day'?'Day Visit':bookingMode==='room'?'Room Stay':'Day Visit + Room Stay'],
 ['Contact', contact],
 ['Date', startDate+(endDate&&endDate!==startDate?' → '+endDate:'')],
 ['Total Pax', pax+' persons'],
 ['Nights', nights],
 ['Salesperson', salesperson],
 ['Status', status],
 ].map(([k,v])=>(
 <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
 <span style={{ color:C.text2 }}>{k}</span>
 <span style={{ color:C.text, fontWeight:600 }}>{v}</span>
 </div>
 ))}
 </div>

 {/* Room summary */}
 {(bookingMode==='room'||bookingMode==='both') && roomAllocations.length>0 && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:18, marginBottom:14 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.blue, marginBottom:10 }}> Room Allocation</div>
 {roomAllocations.map((r,i)=>{
 const rt=roomTypes.find(t=>t.id===r.room_type_id);
 return (
 <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', borderBottom:`1px solid ${C.border}`, fontSize:13 }}>
 <span style={{ color:C.text2 }}>{rt?.name||'Room'} × {r.rooms} rooms × {nights}N · {r.meal_plan}</span>
 <span style={{ color:C.text, fontWeight:600 }}>{inr(r.rate*r.rooms*nights)}</span>
 </div>
 );
 })}
 </div>
 )}

 {/* Meal summary */}
 {Object.entries(meals).some(([,m])=>m.enabled) && (
 <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:12, padding:18, marginBottom:14 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:10 }}> Food Menu</div>
 {Object.entries(meals).filter(([,m])=>m.enabled).map(([meal,m])=>(
 <div key={meal} style={{ marginBottom:6 }}>
 <div style={{ fontWeight:600, fontSize:13, color:C.accent }}>{meal} — {m.pax||pax} pax @ {inr(m.rate)}/pax = {inr((m.rate||0)*(m.pax||pax))}</div>
 {m.items.length>0 && <div style={{ fontSize:11, color:C.text2, marginTop:1 }}>{m.items.join(' · ')}</div>}
 </div>
 ))}
 </div>
 )}

 {/* Financials */}
 <div style={{ background:C.surface, border:`1px solid ${C.accent}44`, borderRadius:12, padding:18 }}>
 <div style={{ fontWeight:700, fontSize:14, color:C.text, marginBottom:12 }}> Financial Summary</div>
 {[
 bookingMode!=='room' ? ['Day Visit Package', inr(dayAmount)] : null,
 bookingMode!=='day' ? ['Room Stay Amount', inr(roomAmount)] : null,
 mealExtra>0 ? ['Meal Extras', inr(mealExtra)] : null,
 actAmount>0 ? ['Activities', inr(actAmount)] : null,
 discountPct>0 ? ['Discount ('+discountPct+'%)', '-'+inr(discount)] : null,
 ['Taxable Amount', inr(taxable)],
 ['GST @ 18%', inr(gst)],
 ].filter(Boolean).map(([k,v])=>(
 <div key={k} style={{ display:'flex', justifyContent:'space-between', padding:'5px 0', fontSize:13 }}>
 <span style={{ color:C.text2 }}>{k}</span>
 <span style={{ color:C.text }}>{v}</span>
 </div>
 ))}
 <div style={{ display:'flex', justifyContent:'space-between', padding:'10px 0 0', borderTop:`1px solid ${C.border}`, marginTop:8, fontSize:18, fontWeight:900 }}>
 <span style={{ color:C.text }}>Total Amount</span>
 <span style={{ color:C.accent }}>{inr(total)}</span>
 </div>
 </div>
 </div>
 )}
 </div>

 {/* Footer */}
 <div style={{ padding:'14px 20px', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', flexShrink:0, background:C.surface }}>
 <button onClick={()=>step>1?setStep(s=>s-1):onClose()}
 style={{ padding:'10px 20px', background:'none', border:`1px solid ${C.border2}`, borderRadius:8, color:C.text2, cursor:'pointer', fontSize:13 }}>
 {step===1?'Cancel':'← Back'}
 </button>
 {step<4
 ? <button onClick={()=>setStep(s=>s+1)}
 style={{ padding:'10px 24px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:13, fontWeight:700 }}>
 Next →
 </button>
 : <button onClick={handleSave} disabled={saving}
 style={{ padding:'10px 24px', background:C.accent, border:'none', borderRadius:8, color:'#fff', cursor:'pointer', fontSize:14, fontWeight:700, opacity:saving?.6:1 }}>
 {saving?'Saving…':bookingToEdit?'Update Booking':' Confirm Booking'}
 </button>
 }
 </div>
 </div>
 </div>
 );
}
